import React, { useState } from "react";
import Row from "@amzn/meridian/row";
import Column from "@amzn/meridian/column";
import Input from "@amzn/meridian/input";
import TimePicker from "@amzn/meridian/time-picker";
import Select, { SelectOption } from "@amzn/meridian/select";
import { TIME_PATTERN } from "../../../util/Constants";
import {
  SSD_DAY_OF_WEEK_CONFIGURATION_ENABLED_ROLES,
  SSD_ROLE,
  SSD_ROLE_NAME,
} from "../SettingsConstant";
import Text from "@amzn/meridian/text";
import {
  isTimeWindowOverlappingWithOthers,
  isValidTimeFormat,
} from "../../SettingsUtil";
import Button from "@amzn/meridian/button";
import Icon from "@amzn/meridian/icon";
import plusCircleTokens from "@amzn/meridian-tokens/base/icon/plus-circle";
import minusCircleTokens from "@amzn/meridian-tokens/base/icon/minus-circle";
import ButtonGroup, { ButtonOption } from "@amzn/meridian/button-group";
import { ModalFooter } from "@amzn/meridian/modal";
import Toaster from "@amzn/meridian/toaster";
import Alert from "@amzn/meridian/alert";
import { isValidNumber } from "../../../util/Util";

const UpsertMinHeadcountRule = ({
  rules,
  rule,
  onSubmit,
  onCancel,
  isEditRule,
}) => {
  const [toasts, setToasts] = useState([]);
  const onCloseToast = (id) => setToasts(toasts.filter((t) => t.id !== id));
  const [updatedRule, setUpdatedRule] = useState(
    JSON.parse(JSON.stringify(rule)),
  );
  const [groupedTimeWindows, setGroupedTimeWindows] = useState(
    getGroupedTimeWindows(JSON.parse(JSON.stringify(rule))),
  );
  const availableRoleNames = Object.values(SSD_ROLE_NAME).filter(
    (roleName) => rules.filter(({ name }) => name === roleName).length === 0,
  );
  if (updatedRule.name && !availableRoleNames.includes(updatedRule.name)) {
    availableRoleNames.push(updatedRule.name);
  }
  const isDayOfWeekConfigurationEnabled =
    SSD_DAY_OF_WEEK_CONFIGURATION_ENABLED_ROLES.includes(updatedRule.name);

  const onChangeRole = (roleName) => {
    updatedRule.name = roleName;
    updatedRule.label = SSD_ROLE[roleName].roleLabel;
    updatedRule.department_name = SSD_ROLE[roleName].departmentName;
    updatedRule.process = SSD_ROLE[roleName].processName;
    updatedRule.businessType = SSD_ROLE[roleName].businessType;
    updatedRule.speed = SSD_ROLE[roleName].speed;
    updatedRule.direct_role = SSD_ROLE[roleName].isDirectRole;
    updatedRule.volume_type = SSD_ROLE[roleName].volumeType;
    updatedRule.order = SSD_ROLE[roleName].order;
    setUpdatedRule({ ...updatedRule });
  };

  const onChangeHeadcount = (groupedTimeWindow, val) => {
    if (val === "-" || val < 0) {
      return false;
    }
    groupedTimeWindow.value = val;
    setGroupedTimeWindows([...groupedTimeWindows]);
    updatedRule.value = getRuleTimeWindows(
      groupedTimeWindows,
      isDayOfWeekConfigurationEnabled,
    );
    setUpdatedRule({ ...updatedRule });
  };

  const onChangeDayOfWeek = (groupedTimeWindow, val) => {
    groupedTimeWindow.isEnabledByDay.forEach((_, i) => {
      groupedTimeWindow.isEnabledByDay[i] = val.includes(i);
    });
    setGroupedTimeWindows([...groupedTimeWindows]);
    updatedRule.value = getRuleTimeWindows(
      groupedTimeWindows,
      isDayOfWeekConfigurationEnabled,
    );
    setUpdatedRule({ ...updatedRule });
  };

  const onChangeStartTime = (groupedTimeWindow, val) => {
    if (isValidTimeFormat(val, TIME_PATTERN)) {
      groupedTimeWindow.start = val.slice(0, -3);
      setGroupedTimeWindows([...groupedTimeWindows]);
      updatedRule.value = getRuleTimeWindows(
        groupedTimeWindows,
        isDayOfWeekConfigurationEnabled,
      );
      setUpdatedRule({ ...updatedRule });
    }
  };
  const onChangeEndTime = (groupedTimeWindow, val) => {
    if (isValidTimeFormat(val, TIME_PATTERN)) {
      groupedTimeWindow.end = val.slice(0, -3);
      setGroupedTimeWindows([...groupedTimeWindows]);
      updatedRule.value = getRuleTimeWindows(
        groupedTimeWindows,
        isDayOfWeekConfigurationEnabled,
      );
      setUpdatedRule({ ...updatedRule });
    }
  };

  const onAddTimeWindow = () => {
    groupedTimeWindows.push({
      value: 0,
      start: "",
      end: "",
      isEnabledByDay: [true, true, true, true, true, true, true],
    });
    setGroupedTimeWindows([...groupedTimeWindows]);
  };

  const onDeleteTimeWindow = (groupedTimeWindow) => {
    const updatedGroupedTimeWindows = groupedTimeWindows.filter(
      (gtw) => gtw !== groupedTimeWindow,
    );
    setGroupedTimeWindows([...updatedGroupedTimeWindows]);
    updatedRule.value = getRuleTimeWindows(
      updatedGroupedTimeWindows,
      isDayOfWeekConfigurationEnabled,
    );
    setUpdatedRule({ ...updatedRule });
  };

  const onClickSubmit = () => {
    if (!updatedRule.name) {
      return setToasts(
        toasts.concat({
          id: Math.random(),
          message: `Please select a role.`,
        }),
      );
    }
    if (
      groupedTimeWindows.filter(
        ({ value }) => !isValidNumber(value) || value < 0,
      ).length > 0
    ) {
      return setToasts(
        toasts.concat({
          id: Math.random(),
          message: `Please enter a non-negative value for min headcount.`,
        }),
      );
    }
    if (
      groupedTimeWindows.filter(
        ({ start, end }) =>
          !isValidTimeFormat(start + ":00", TIME_PATTERN) ||
          !isValidTimeFormat(end + ":00", TIME_PATTERN),
      ).length > 0
    ) {
      return setToasts(
        toasts.concat({
          id: Math.random(),
          message: `Please enter the missing start and end times.`,
        }),
      );
    }
    if (
      groupedTimeWindows.filter(({ start, end }) => start === end).length > 0
    ) {
      return setToasts(
        toasts.concat({
          id: Math.random(),
          message: `Start and end times cannot be identical`,
        }),
      );
    }
    if (isGroupedTimeWindowsOverlapping(groupedTimeWindows)) {
      return setToasts(
        toasts.concat({
          id: Math.random(),
          message: `Please enter non-overlapping time windows.`,
        }),
      );
    }
    onSubmit(updatedRule);
    onCancel();
  };

  return (
    <Column>
      <React.Fragment>
        <Toaster toasts={toasts} onCloseToast={onCloseToast}>
          {(toast) => (
            <Alert toast={true} onClose={toast.onClose} type="error">
              {toast.message}
            </Alert>
          )}
        </Toaster>
      </React.Fragment>
      <Row alignmentHorizontal="center">
        <Column>
          <Text tag="label" type="b200" color="primary">
            Role *
          </Text>
          <Select
            value={updatedRule.name}
            onChange={onChangeRole}
            placeholder="Select a role"
            width={200}
            disabled={isEditRule}
          >
            {availableRoleNames.map((roleName) => (
              <SelectOption
                value={roleName}
                label={SSD_ROLE[roleName].roleLabel}
              />
            ))}
          </Select>
        </Column>
        <Column>
          <Row height={10} />
          <Text>requires</Text>
        </Column>
      </Row>
      <div
        style={{ width: "100%", height: 1, backgroundColor: "rgba(0,0,0,0.1)" }}
      />
      {groupedTimeWindows.map((groupedTimeWindow, i) => {
        const isWeekWide =
          groupedTimeWindow.isEnabledByDay.filter((isEnabled) => !isEnabled)
            .length === 0;
        return (
          <Row width="100%">
            <Column>
              <Text tag="label" type="b200" color="primary">
                Min. Headcount *
              </Text>
              <Input
                value={groupedTimeWindow.value}
                onChange={(val) => onChangeHeadcount(groupedTimeWindow, val)}
                type="number"
                width={150}
              />
            </Column>
            {(!isWeekWide || isDayOfWeekConfigurationEnabled) && (
              <Column>
                <Text tag="label" type="b200" color="primary">
                  Day(s) Select*
                </Text>
                <ButtonGroup
                  value={groupedTimeWindow.isEnabledByDay
                    .map((isEnabled, i) => (isEnabled ? i : -1))
                    .filter((i) => i >= 0)}
                  onChange={(val) => onChangeDayOfWeek(groupedTimeWindow, val)}
                  size="medium"
                >
                  <ButtonOption value={0}>S</ButtonOption>
                  <ButtonOption value={1}>M</ButtonOption>
                  <ButtonOption value={2}>T</ButtonOption>
                  <ButtonOption value={3}>W</ButtonOption>
                  <ButtonOption value={4}>T</ButtonOption>
                  <ButtonOption value={5}>F</ButtonOption>
                  <ButtonOption value={6}>S</ButtonOption>
                </ButtonGroup>
              </Column>
            )}
            <Column>
              <Text tag="label" type="b200" color="primary">
                Start time *
              </Text>
              <TimePicker
                placeholder="MM:HH"
                value={groupedTimeWindow.start + ":00"}
                onChange={(val) => onChangeStartTime(groupedTimeWindow, val)}
                width={200}
              />
            </Column>
            <Column>
              <Text tag="label" type="b200" color="primary">
                End time *
              </Text>
              <TimePicker
                placeholder="MM:HH"
                value={groupedTimeWindow.end + ":00"}
                onChange={(val) => onChangeEndTime(groupedTimeWindow, val)}
                width={200}
              />
            </Column>
            <Column>
              <Row height={15} />
              <Row>
                {groupedTimeWindows.length > 1 && (
                  <Button
                    type="icon"
                    onClick={() => onDeleteTimeWindow(groupedTimeWindow)}
                  >
                    <Text color="error">
                      <Icon tokens={minusCircleTokens} />
                    </Text>
                  </Button>
                )}
                {i === groupedTimeWindows.length - 1 && (
                  <Button type="icon" onClick={onAddTimeWindow}>
                    <div style={{ color: "#63a63a" }}>
                      <Icon tokens={plusCircleTokens} />
                    </div>
                  </Button>
                )}
              </Row>
            </Column>
          </Row>
        );
      })}
      <ModalFooter>
        <Row alignmentHorizontal="end" widths="fit">
          <Button type="secondary" size="small" onClick={onCancel}>
            Close
          </Button>
          <Button type="primary" size="small" onClick={onClickSubmit}>
            Confirm
          </Button>
        </Row>
      </ModalFooter>
    </Column>
  );
};

const getGroupedTimeWindows = (rule) => {
  if (rule.value == null) {
    return [];
  }
  const timeWindows = [];
  if (!isNaN(rule.value)) {
    timeWindows.push({ value: rule.value, start: "00:00", end: "23:59" });
  } else if (rule.value.value != null) {
    timeWindows.push({
      value: rule.value.value,
      ...(rule.value.start && rule.value.end
        ? { start: rule.value.start, end: rule.value.end }
        : { start: "00:00", end: "23:59" }),
      ...(isValidNumber(rule.value.day_of_week)
        ? { day_of_week: rule.value.day_of_week }
        : {}),
    });
  } else if (Array.isArray(rule.value)) {
    timeWindows.push(...rule.value);
  } else {
    return [];
  }
  const timeWindowMap = {};
  timeWindows.forEach((timeWindow) => {
    const { day_of_week, start, end, value } = timeWindow;
    const key = start + "-" + end;
    if (!timeWindowMap[key]) {
      timeWindowMap[key] = {
        value,
        start,
        end,
        isEnabledByDay: [false, false, false, false, false, false, false],
      };
    }
    if (day_of_week == null) {
      timeWindowMap[key].isEnabledByDay = [
        true,
        true,
        true,
        true,
        true,
        true,
        true,
      ];
    } else {
      timeWindowMap[key].isEnabledByDay[day_of_week] = true;
    }
  });
  return Object.values(timeWindowMap);
};

const isGroupedTimeWindowsOverlapping = (groupedTimeWindows) => {
  return (
    groupedTimeWindows.filter((groupedTimeWindow) => {
      const otherGroupedTimeWindows = groupedTimeWindows.filter(
        (otherGroupedTimeWindow) =>
          otherGroupedTimeWindow !== groupedTimeWindow,
      );
      return isTimeWindowOverlappingWithOthers(
        {
          startTime: groupedTimeWindow.start + ":00",
          endTime: groupedTimeWindow.end + ":00",
        },
        otherGroupedTimeWindows.map(({ start, end }) => ({
          startTime: start + ":00",
          endTime: end + ":00",
        })),
      );
    }).length > 0
  );
};

const getRuleTimeWindows = (
  groupedTimeWindows,
  isDayOfWeekConfigurationEnabled,
) => {
  const timeWindows = [];
  groupedTimeWindows.forEach((groupedTimeWindow) => {
    if (!isValidNumber(groupedTimeWindow.value)) {
      return false;
    }
    if (!groupedTimeWindow.start || !groupedTimeWindow.end) {
      return false;
    }
    const isWeekWide =
      groupedTimeWindow.isEnabledByDay.filter((isEnabled) => !isEnabled)
        .length === 0;
    const timeWindow = {
      value: parseInt(groupedTimeWindow.value, 10),
      start: groupedTimeWindow.start,
      end: groupedTimeWindow.end,
    };
    if (isWeekWide && !isDayOfWeekConfigurationEnabled) {
      timeWindows.push({
        ...timeWindow,
      });
    } else {
      groupedTimeWindow.isEnabledByDay.forEach((isEnabled, i) => {
        if (isEnabled) {
          timeWindows.push({
            ...timeWindow,
            day_of_week: i,
          });
        }
      });
    }
  });
  return timeWindows;
};

export default UpsertMinHeadcountRule;



// WEBPACK FOOTER //
// ./src/components/settings/settingsv2/ssd/UpsertMinHeadcountRule.js